import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import {
  LayoutDashboard,
  Boxes,
  GitMerge,
  Ship,
  ShoppingCart,
  FileBarChart2,
  LogOut,
  Languages,
  ArrowLeftRight,
  PanelLeftClose,
  PanelLeftOpen,
  UserCog,
  History,
  KeyRound,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ChangePasswordDialog } from "@/components/users/PasswordDialogs";

const navItems = (t) => [
  { to: "/", label: t("dashboard"), icon: LayoutDashboard, testid: "nav-dashboard-link", module: "dashboard" },
  { to: "/master-data", label: t("master_data"), icon: Boxes, testid: "nav-master-data-link", module: "masters" },
  { to: "/mappings", label: t("mappings"), icon: GitMerge, testid: "nav-mappings-link", module: "mappings" },
  { to: "/inventory", label: t("inventory"), icon: ArrowLeftRight, testid: "nav-inventory-link", module: "stock" },
  { to: "/shipments", label: t("shipments"), icon: Ship, testid: "nav-shipments-link", module: "shipments" },
  { to: "/purchasing", label: t("purchasing"), icon: ShoppingCart, testid: "nav-purchasing-link", module: "purchasing" },
  { to: "/reports", label: t("reports"), icon: FileBarChart2, testid: "nav-reports-link", module: "reports" },
  { to: "/users", label: t("users_mgmt"), icon: UserCog, testid: "nav-users-link", adminOnly: true },
  { to: "/audit", label: t("audit_log"), icon: History, testid: "nav-audit-link", module: "audit" },
];

export default function Shell({ children }) {
  const { user, logout, can } = useAuth();
  const { lang, setLang, t } = useLang();
  const nav = useNavigate();
  const [pwOpen, setPwOpen] = useState(false);
  const [mini, setMini] = useState(() => localStorage.getItem("sidebar_mini") === "1");
  const toggleMini = () => { setMini(!mini); localStorage.setItem("sidebar_mini", mini ? "0" : "1"); };
  const items = navItems(t).filter((i) => (i.adminOnly ? user?.role === "admin" : can(i.module)));

  return (
    <div className="h-screen overflow-hidden flex bg-slate-50 text-slate-800" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      {/* Sidebar */}
      <aside
        className={`${mini ? "w-16" : "w-64"} bg-slate-900 text-slate-100 flex-shrink-0 flex-col justify-between hidden md:flex border-r border-slate-800 transition-[width] duration-200 overflow-hidden`}
        data-testid="sidebar"
      >
        <div className="min-h-0 flex flex-col">
          <div className={`h-16 flex items-center border-b border-slate-800 ${mini ? "justify-center px-2" : "px-4 justify-between"}`}>
            {!mini && (
              <div className="flex items-center">
                <div className="w-9 h-9 rounded-md bg-blue-600 flex items-center justify-center font-bold text-white mr-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  K
                </div>
                <div className="flex flex-col">
                  <span className="font-bold tracking-tight text-base" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    K-LOGIC
                  </span>
                  <span className="text-[10px] leading-tight text-slate-400 max-w-[150px]">KAM Logistics, Order &amp; General Inventory Control</span>
                </div>
              </div>
            )}
            <button type="button" onClick={toggleMini} title={mini ? t("expand_menu") : t("minimize_menu")}
                    className="h-8 w-8 inline-flex items-center justify-center rounded-md text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
                    data-testid="sidebar-toggle-btn">
              {mini ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
            </button>
          </div>
          <nav className="p-2 space-y-1 overflow-y-auto">
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                title={mini ? item.label : undefined}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 py-2.5 rounded-md text-sm font-medium transition-colors ${mini ? "justify-center px-0" : "px-3"} ${
                    isActive
                      ? "bg-blue-600 text-white"
                      : "text-slate-300 hover:bg-slate-800 hover:text-white"
                  }`
                }
              >
                <item.icon size={18} className="flex-shrink-0" />
                {!mini && <span className="whitespace-nowrap">{item.label}</span>}
              </NavLink>
            ))}
          </nav>
        </div>
        <div className={`border-t border-slate-800 ${mini ? "p-2 text-center" : "p-4"}`}>
          {mini ? (
            <div className="mx-auto w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center text-xs font-bold uppercase" title={`${user?.name} · ${user?.role}`}>
              {(user?.name || "?").slice(0, 2)}
            </div>
          ) : (
            <>
              <div className="text-xs text-slate-400 uppercase tracking-wider mb-2">{t("hello")}</div>
              <div className="text-sm font-medium">{user?.name}</div>
              <div className="text-xs text-slate-400 uppercase tracking-wider">{user?.role}</div>
            </>
          )}
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        {/* Header */}
        <header className="h-16 flex-shrink-0 bg-slate-900 border-b border-slate-800 text-white flex items-center justify-between px-6 z-40">
          <div className="flex items-center">
            <h2 className="font-semibold tracking-tight text-base md:hidden" style={{ fontFamily: "'Outfit', sans-serif" }}>
              K-LOGIC
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setLang(lang === "id" ? "en" : "id")}
              className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-slate-800 hover:bg-slate-700 rounded-md transition-colors"
              data-testid="language-toggle-button"
            >
              <Languages size={14} />
              <span className="uppercase">{lang}</span>
            </button>
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-slate-800 rounded-md text-xs">
              <span className="text-slate-400">{t("role")}:</span>
              <span className="uppercase font-semibold tracking-wide">{user?.role}</span>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setPwOpen(true)}
                    className="text-slate-200 hover:bg-slate-800 hover:text-white" data-testid="change-password-button">
              <KeyRound size={16} className="sm:mr-2" /><span className="hidden sm:inline">{t("change_password")}</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={async () => { await logout(); nav("/login"); }}
              className="text-slate-200 hover:bg-slate-800 hover:text-white"
              data-testid="logout-button"
            >
              <LogOut size={16} className="mr-2" />
              {t("logout")}
            </Button>
          </div>
        </header>

        <main className="flex-1 min-h-0 p-6 md:p-8 overflow-y-auto bg-slate-50" data-testid="main-scroll-area">{children}</main>
      </div>
      <ChangePasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} />
    </div>
  );
}
